type Primitive = string | number | boolean | null | undefined;

type SettingsValue = Primitive | unknown[];

interface Settings {
  [key: string]: SettingsValue | Settings;
}

interface Change {
  path: string;
  prev: SettingsValue;
  next: SettingsValue;
}

type LogFn = (message: string, data: any) => void;

interface GetChangesOptions {
  prev: Settings;
  next: Settings;
  logFn?: LogFn;
}

export function getChanges({ prev, next, logFn }: GetChangesOptions): Change[] {
  const changes: Change[] = [];

  for (const key in next) {
    if (!Object.prototype.hasOwnProperty.call(next, key)) {
      continue;
    }
    if (prev[key] === null && next[key] === null) {
      continue;
    }
    const nextValue = next[key];
    const prevValue = prev[key];
    const typeofNext = typeof nextValue;
    const typeofPrev = typeof prevValue;
    if (typeofNext !== typeofPrev) {
      if (
        typeofNext === "string" &&
        typeofPrev === "object" &&
        prevValue?.toString() == nextValue
      ) {
        // Handle objectIds
        continue;
      }

      if (prevValue instanceof Date) {
        if (prevValue.toISOString() !== nextValue) {
          changes.push({
            path: key,
            prev: prevValue as any,
            next: nextValue as any,
          });
        }
        continue;
      }

      if (logFn) {
        logFn("typemissmatch", {
          key,
          typeofNext,
          typeofPrev,
          nextValue,
          prevValue,
        });
      }
      changes.push({
        path: key,
        prev: prevValue as any,
        next: nextValue as any,
      });
      continue;
    }
    switch (typeof nextValue) {
      case "string":
      case "number":
      case "boolean":
      case "undefined":
        if (prev[key] != next[key]) {
          changes.push({
            path: key,
            prev: prevValue as typeof nextValue,
            next: nextValue,
          });
        }
        break;
      case "object":
        if (Array.isArray(nextValue)) {
          if (JSON.stringify(prevValue) !== JSON.stringify(nextValue)) {
            changes.push({
              path: key,
              prev: prevValue as typeof nextValue,
              next: nextValue,
            });
          }
        } else if (nextValue == null && prevValue != null) {
          changes.push({ path: key, prev: prevValue as any, next: nextValue });
        } else if (nextValue !== null && prevValue == null) {
          changes.push({
            path: key,
            prev: prevValue as any,
            next: nextValue as any,
          });
        } else {
          const nestedChanges = getChanges({
            prev: prevValue as Settings,
            next: nextValue as Settings,
            logFn,
          });
          changes.push(
            ...nestedChanges.map((change) => ({
              path: `${key}.${change.path}`,
              prev: change.prev,
              next: change.next,
            })),
          );
        }
        break;
      default:
        throw new Error(`Unexpected value in settings: ${nextValue}`);
    }
  }

  return changes;
}

export type { Change, Settings, SettingsValue };
